// Plex-ID text helpers + Sentry captureMessage, split out from lib/utils.js so
// markdown/interpolation.js (which utils.js imports, via interpolateEntityFields)
// can use them without importing back into utils.js and forming a cycle.
import * as Sentry from "@sentry/react";

export const RE_PLEX_ID = /^(?!https?:)[a-z][-_a-z0-9]+:[^\s,;'"\\]+$/;

export const safeStringifyJSON = obj => {
  try {
    return JSON.stringify(obj);
  }
  catch(e) {
    return `${obj}`;
  }
}

/**
 * Capture a message or exception to Sentry with optional attachments and context
 *
 * @param {string} message - The message or error description
 * @param {string|object} levelOrOptions - Severity level (info, warning, error, etc.) or options object
 * @param {object} data - Additional data to attach (legacy parameter, use options.data instead)
 * @returns {Promise<string>} - Promise resolving to the Sentry event ID
 *
 * Options object:
 * - level: Severity level (default: "error")
 * - asException: Send as exception instead of message (default: false)
 * - errorName: Custom error name for exceptions (default: "UserException")
 * - fingerprint: Custom fingerprint array to control event grouping (default: auto)
 * - tags: Object with tags to add to the event
 * - context: Object with context data
 * - attachments: Array of attachment objects {filename, data, contentType}
 * - data: Data to attach as data.json (legacy)
 */
export const captureMessage = (message, levelOrOptions = "error", data = null) => {
  try {
    let options = {};
    if (typeof levelOrOptions === "string") {
      options.level = levelOrOptions;
      options.data = data;
    } else {
      options = levelOrOptions || {};
    }

    const {
      level = "error",
      asException = false,
      errorName = null,
      fingerprint = null,
      tags = {},
      context = {},
      extra = null,
      attachments = [],
      data: optionsData = null,
      captureStackTrace = true
    } = options;

    const client = Sentry.getClient();
    const clientOptions = client?.getOptions();

    // CRITICAL FIX: Sentry SDK disables manual capture calls for localhost
    // Use console.error workaround which bypasses the enabled check
    if (!clientOptions?.enabled) {
      const error = new Error(message);
      error.name = errorName || (asException ? "UserException" : "UserMessage");
      if (Object.keys(tags).length > 0) error.tags = tags;
      if (Object.keys(context).length > 0) error.context = context;
      if (optionsData) error.data = optionsData;
      if (attachments.length > 0) error.attachments = attachments;
      console.error(error);
      return null;
    }

    let eventId;
    Sentry.withScope(scope => {
      if (fingerprint) scope.setFingerprint(fingerprint);

      const currentUser = Sentry.getCurrentScope().getUser();
      if (!currentUser) {
        const lastUsername = localStorage.getItem('plex-last-username');
        if (lastUsername) scope.setTag('best_guess_user', lastUsername);
      }

      Object.entries(tags).forEach(([key, value]) => scope.setTag(key, value));
      Object.entries(context).forEach(([key, value]) => scope.setContext(key, value));
      if (extra && typeof extra === "object") scope.setExtras(extra);
      attachments.forEach(attachment => scope.addAttachment(attachment));

      if (optionsData) {
        scope.addAttachment({
          filename: "data.json",
          data: typeof optionsData === "string" ? optionsData : safeStringifyJSON(optionsData),
          contentType: "application/json",
        });
      }

      if (asException) {
        const error = new Error(message);
        error.name = errorName || "UserException";
        if (!captureStackTrace) delete error.stack;
        eventId = Sentry.captureException(error);
      } else {
        const normalizedLevel = /^(log|info|warning|error|fatal|debug|assert)$/.test(level) ? level : "info";
        eventId = Sentry.captureMessage(message, normalizedLevel);
      }
    });

    return eventId;
  } catch (error) {
    console.error(`[Sentry] captureMessage failed:`, error);
    return null;
  }
};

// A link's tooltip. Plex IDs show as they are, except where readers know the record by another
// name: "pubmed:14636569" is "PMID 14636569" (PLEX-6500). The ID itself stays in data-plex-id.
export const readableIdTitle = id => {
  const pmid = typeof id === "string" && id.match(/^pubmed:(\d+)$/);
  return pmid ? `PMID ${pmid[1]}` : id;
};

export const stylizePublications = s => {
  return s.replace(/\[([^\]]+)](?=\(pubmed:)/g, (match, text) => RE_PLEX_ID.test(text) ? `[${text}]` : `["${text}"]`)
    .replace(/\[([^\]]+)](?=\(patents:)/g, (match, text) => RE_PLEX_ID.test(text) ? `[${text}]` : `[*${text}*]`);
};

// Split text into alternating [prose, protected, prose, protected, ...] segments.
// Protected segments are fenced code blocks (```...```), inline code (`...`), and
// raw XML/HTML blocks that LLMs sometimes emit without fences (e.g. GraphML, SVG).
// Returns [{text, protected: bool}].
const _RE_PROTECTED_SEGMENTS = new RegExp(
  // Fenced code block (``` ... ```)
  '```[\\s\\S]*?```'
  // Inline code span (` ... ` on one line)
  + '|`[^`\\n]+`'
  // Raw XML: optional <?xml ...?> declaration followed by a recognised root element
  // Matches from <?xml or <rootElement through the matching </rootElement>
  + '|(?:' + '<\\?xml[^?]*\\?>[\\s\\S]*?)?'
    + '<(graphml|svg|html|rss|feed|opml)\\b[\\s\\S]*?<\\/\\1>'
  , 'g'
);

const splitProtectedSegments = s => {
  const segments = [];
  // Reset lastIndex since we reuse the compiled regex
  _RE_PROTECTED_SEGMENTS.lastIndex = 0;
  let last = 0;
  let m;
  while ((m = _RE_PROTECTED_SEGMENTS.exec(s)) !== null) {
    if (m.index > last) segments.push({ text: s.slice(last, m.index), protected: false });
    segments.push({ text: m[0], protected: true });
    last = m.index + m[0].length;
  }
  if (last < s.length) segments.push({ text: s.slice(last), protected: false });
  return segments;
};

export const linkifyPlexIds = s => {
  // Convert to MD links any bare plex IDs (not already within an MD link).
  // Skips fenced/inline code blocks and raw XML blocks to avoid corrupting structured data.
  return splitProtectedSegments(s).map(seg => {
    if (seg.protected) return seg.text;
    return seg.text.replace(/(?<!]\()(?<!\[)(\b[a-z][-_0-9a-z]+:[^\s,;'"\\)\]]+\b)(?!\])/mg,
                     (match, id, offset) => {
                       const beforeText = seg.text.substring(0, offset);
                       const openBrackets = (beforeText.match(/\[/g) || []).length;
                       const closeBrackets = (beforeText.match(/\]/g) || []).length;
                       if (openBrackets > closeBrackets) return match;
                       if (/\]\([^)]*$/.test(beforeText)) return match;
                       // Skip IDs inside HTML/JSX-style tag attributes (e.g.
                       // `<VizChip id="..."/>`). Chart IDs that contain a colon
                       // (chartUuid:tooluseId) otherwise get matched as Plex IDs
                       // and the chip tag is corrupted by [..](..) wrapping.
                       if (beforeText.lastIndexOf("<") > beforeText.lastIndexOf(">")) return match;
                       if (!RE_PLEX_ID.test(id)) return match;
                       return `[${id}](${id})`;
                     });
  }).join('');
}
